/* ═══════════════════════════════════════════════════════════════════════════
   La perte de contexte WebGL (PLAN.md §4.10).

   C'est le mode de panne n°1 sur mobile, et il ne ressemble pas à une panne :
   aucune exception, aucun message dans la console, aucun crash. Le canvas
   devient noir et TOUT LE RESTE continue de fonctionner — HUD DOM, audio,
   écrans React, simulation. Le symptôme observable n'oriente donc vers aucune
   cause, exactement comme la régression de dimensionnement du J8.

   Trois déclencheurs ordinaires, dont aucun n'est un bug du jeu :

     - ONGLET EN ARRIÈRE-PLAN. iOS Safari et Chrome Android libèrent le
       contexte des onglets inactifs. Revenir sur l'onglet après deux minutes
       est un geste banal, pas un cas limite.

     - PRESSION MÉMOIRE. Cette architecture y est particulièrement exposée
       puisque TOUT est pré-alloué au démarrage : flipbooks 4096², pools
       d'InstancedMesh en DynamicDrawUsage, plusieurs render targets HalfFloat,
       catalogue de programmes du warm-up. On est un candidat de choix quand le
       système cherche de la mémoire à reprendre.

     - CHANGEMENT DE GPU À CHAUD. Bascule iGPU/dGPU sur portable, mise à jour
       ou reset du pilote, branchement d'un écran externe. Rare sur une machine
       de développement, ordinaire sur le parc réel.

   ⚠️ `event.preventDefault()` N'EST PAS NÉGOCIABLE.

   La spécification WebGL est explicite : `webglcontextrestored` n'est émis QUE
   si le gestionnaire de `webglcontextlost` a annulé l'événement. Sans cet
   appel, le navigateur comprend que la page renonce au contexte et ne tentera
   jamais de le restaurer. La panne passe de temporaire à DÉFINITIVE, et le
   seul remède restant est un rechargement de page — c'est-à-dire la perte de
   la run en cours.

   C'est aussi la raison pour laquelle `preventDefault()` est appelé AVANT
   toute notification d'abonné : un callback qui jette ne doit pas pouvoir
   emporter avec lui la seule ligne qui rend la récupération possible.

   Ce module ne reconstruit rien lui-même. Il ne connaît ni three, ni le
   composer, ni les pools — il signale et il fige. La reconstruction appartient
   à l'appelant, qui seul sait dans quel ordre republier ses ressources.
   ═══════════════════════════════════════════════════════════════════════════ */

export type ContextLossCallback = () => void;

/** Le strict nécessaire d'un canvas pour ce module. Structurel plutôt que
 *  `HTMLCanvasElement` : les tests fournissent un double, et rien d'autre n'est
 *  utilisé ici. */
export interface ContextLossCanvas {
  addEventListener(type: string, listener: (event: Event) => void): void;
  removeEventListener(type: string, listener: (event: Event) => void): void;
  getContext?(contextId: string): unknown;
}

/** Le strict nécessaire d'un contexte GL. `WebGLRenderingContext` et
 *  `WebGL2RenderingContext` le satisfont tous deux. */
export interface GlContextProbe {
  getExtension(name: string): unknown;
}

/** Ce que le garde a besoin de savoir faire à la boucle de jeu. `Loop` la
 *  satisfait déjà, structurellement, sans que ce module ait à l'importer — la
 *  règle de dépendance interdirait de toute façon le lien inverse. */
export interface Freezable {
  stop(): void;
}

interface LoseContextExtension {
  loseContext(): void;
  restoreContext(): void;
}

function isLoseContextExtension(x: unknown): x is LoseContextExtension {
  if (typeof x !== 'object' || x === null) return false;
  const e = x as Partial<LoseContextExtension>;
  return typeof e.loseContext === 'function' && typeof e.restoreContext === 'function';
}

export class ContextLossGuard {
  private readonly canvas: ContextLossCanvas;
  private readonly gl: GlContextProbe | null;
  private readonly lostCbs: ContextLossCallback[] = [];
  private readonly restoredCbs: ContextLossCallback[] = [];
  private isLost = false;
  private disposed = false;

  /** Nombre de pertes depuis le démarrage. Destiné à la télémétrie (§7.6) : la
   *  fréquence réelle des pertes par plateforme ne s'observe pas autrement, et
   *  c'est précisément le chiffre qui dira si cette page de code sert à
   *  quelque chose. */
  losses = 0;

  /**
   * @param canvas Le canvas du renderer (`renderer.domElement`).
   * @param gl Contexte GL déjà obtenu, pour `forceLose`/`forceRestore`. Le
   *   passer évite un `getContext()` de redécouverte ; son absence n'empêche
   *   que ces deux helpers de test, jamais la détection elle-même.
   */
  constructor(canvas: ContextLossCanvas, gl: GlContextProbe | null = null) {
    this.canvas = canvas;
    this.gl = gl;
    this.handleLost = this.handleLost.bind(this);
    this.handleRestored = this.handleRestored.bind(this);
    canvas.addEventListener('webglcontextlost', this.handleLost);
    canvas.addEventListener('webglcontextrestored', this.handleRestored);
  }

  /** `true` entre la perte et la restauration. Tant que c'est vrai, aucun appel
   *  de rendu n'a de sens : les commandes émises sur un contexte perdu sont
   *  ignorées en silence. */
  get lost(): boolean {
    return this.isLost;
  }

  /** Enregistre un abonné à la perte. Retourne de quoi se désabonner — utile
   *  pour un écran qui va et vient sans faire fuir ses callbacks. */
  onLost(cb: ContextLossCallback): () => void {
    this.lostCbs.push(cb);
    return () => remove(this.lostCbs, cb);
  }

  onRestored(cb: ContextLossCallback): () => void {
    this.restoredCbs.push(cb);
    return () => remove(this.restoredCbs, cb);
  }

  /**
   * Fige la boucle à la perte. C'est le geste minimal à ne pas oublier : sans
   * lui, `requestAnimationFrame` continue d'appeler `render()` sur un contexte
   * mort, ce qui consomme du CPU et de la batterie pour dessiner du néant, et
   * fait avancer la simulation pendant que le joueur regarde un écran noir.
   *
   * ⚠️ La reprise n'est PAS automatique, et c'est délibéré. La reconstruction
   * (republication des textures, `composer` recréé, buffers d'instances,
   * warm-up) est asynchrone : redémarrer la boucle ici la ferait rendre pendant
   * que les ressources sont encore à moitié reconstruites. C'est donc à
   * l'appelant de rappeler `loop.start()` à la FIN de sa séquence de
   * restauration — et de repasser en PAUSE plutôt qu'en jeu, réinjecter le
   * joueur au milieu d'un rideau après une seconde de reconstruction étant une
   * mort injuste garantie (§4.10).
   */
  freeze(target: Freezable): () => void {
    return this.onLost(() => target.stop());
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.canvas.removeEventListener('webglcontextlost', this.handleLost);
    this.canvas.removeEventListener('webglcontextrestored', this.handleRestored);
    this.lostCbs.length = 0;
    this.restoredCbs.length = 0;
  }

  /* ── Helpers de TEST ────────────────────────────────────────────────────
     `WEBGL_lose_context` est le seul moyen de déclencher la panne à la
     demande : l'attendre en conditions réelles suppose de saturer la mémoire
     d'un téléphone, ce qui n'est ni reproductible ni tenable dans une boucle
     de développement. Sans ces deux méthodes, le chemin de restauration ne
     serait jamais exercé avant de partir en production.

     ⚠️ Elles sont ASYNCHRONES du point de vue de l'appelant : `loseContext()`
     ne fait que demander la perte, l'événement `webglcontextlost` arrive à un
     tour de boucle ultérieur. `forceLose()` retournant `true` ne signifie donc
     pas que `lost` est déjà passé à `true`.

     ⚠️ `restoreContext()` doit être appelée APRÈS que l'événement de perte a
     été traité. Trop tôt, elle lève INVALID_OPERATION. */

  /** @returns `false` si l'extension est indisponible — cas normal hors d'un
   *  vrai navigateur, et sur les pilotes qui ne l'exposent pas. */
  forceLose(): boolean {
    const ext = this.extension();
    if (!ext) return false;
    try {
      ext.loseContext();
      return true;
    } catch {
      return false;
    }
  }

  forceRestore(): boolean {
    const ext = this.extension();
    if (!ext) return false;
    try {
      ext.restoreContext();
      return true;
    } catch {
      return false;
    }
  }

  /* ── Interne ────────────────────────────────────────────────────────── */

  private handleLost(event: Event): void {
    /* ⚠️ PREMIÈRE LIGNE, avant tout le reste. Voir l'en-tête du module : sans
       cet appel, `webglcontextrestored` ne sera jamais émis et la panne devient
       définitive. La placer après la notification des abonnés suffirait à la
       perdre le jour où l'un d'eux jette. */
    event.preventDefault();

    if (this.isLost) return;
    this.isLost = true;
    this.losses++;
    emit(this.lostCbs, 'onLost');
  }

  private handleRestored(): void {
    // Pas de garde sur `isLost` : un garde construit APRÈS la perte n'a jamais
    // vu l'événement correspondant, et doit malgré tout déclencher la
    // reconstruction. Un `webglcontextrestored` spontané n'existe pas.
    this.isLost = false;
    emit(this.restoredCbs, 'onRestored');
  }

  private extension(): LoseContextExtension | null {
    const gl = this.gl ?? this.resolveGl();
    if (!gl) return null;
    try {
      const ext = gl.getExtension('WEBGL_lose_context');
      return isLoseContextExtension(ext) ? ext : null;
    } catch {
      return null;
    }
  }

  /** Redécouvre le contexte depuis le canvas. `getContext` avec le même type
   *  rend le contexte DÉJÀ créé, il n'en alloue pas un second. */
  private resolveGl(): GlContextProbe | null {
    const get = this.canvas.getContext;
    if (typeof get !== 'function') return null;
    try {
      const ctx = get.call(this.canvas, 'webgl2') ?? get.call(this.canvas, 'webgl');
      if (typeof ctx !== 'object' || ctx === null) return null;
      const probe = ctx as Partial<GlContextProbe>;
      return typeof probe.getExtension === 'function' ? (ctx as GlContextProbe) : null;
    } catch {
      return null;
    }
  }
}

function remove(list: ContextLossCallback[], cb: ContextLossCallback): void {
  const i = list.indexOf(cb);
  if (i >= 0) list.splice(i, 1);
}

function emit(list: readonly ContextLossCallback[], label: string): void {
  // Itération sur une COPIE : un abonné a le droit d'appeler `dispose()` ou de
  // se désabonner depuis son propre callback, et parcourir la liste vivante
  // sauterait alors l'abonné suivant.
  for (const cb of list.slice()) {
    try {
      cb();
    } catch (err) {
      // Un abonné défaillant ne doit pas emporter les suivants : c'est la
      // séquence de récupération qui est en jeu, et son premier maillon est
      // l'arrêt de la boucle. On journalise plutôt que d'avaler en silence.
      console.error(`[ContextLossGuard] un abonné ${label} a jeté`, err);
    }
  }
}
